import { AnyAction } from 'redux';
import { Injectable } from '@angular/core';
import {
  Produktlinie,
  TierartEnum,
  TkvV2Vorbelegungdaten,
  ValueLabelElement,
} from '@ps20-frontend/frontend-models';
import {
  GlobalAction,
  ProdukttabelleEnum,
  TARIF_CHECKED,
} from '@ps20-frontend/shop-integration-redux';
import {
  Heilbehandlungsschutz,
  Produktlinienname,
  Selbstbeteiligung,
  Tiergeschlecht,
  Tierhaltung,
} from '@ps20/bff-models';
import { DogBreedType, DogSize } from './reducers/versicherungsnehmer.reducer';
import { AntragsummerGenerated } from './actiontypes/antragsummerGenerated';
import { CoverageType } from './models/vertragsoptionen.model';

export interface TkvAction<T = undefined> extends AnyAction {
  payload?: T;
}

@Injectable()
export class TkvActions {
  static readonly BACK_TO_SHOP_MODAL_OPEN = 'BACK_TO_SHOP_MODAL_OPEN';
  static readonly BACK_TO_SHOP_MODAL_CLOSE = 'BACK_TO_SHOP_MODAL_CLOSE';
  static readonly ANIMAL_TYPE_CHANGED = 'ANIMAL_TYPE_CHANGED';
  static readonly ANIMAL_GENDER_CHANGED = 'ANIMAL_GENDER_CHANGED';
  static readonly ANIMAL_NAME_CHANGED = 'ANIMAL_NAME_CHANGED';
  static readonly ANIMAL_BIRTH_DATE_CHANGED = 'ANIMAL_BIRTH_DATE_CHANGED';
  static readonly BREED_CHANGED = 'BREED_CHANGED';
  static readonly DOG_BREED_TYPE_CHANGED = 'DOG_BREED_TYPE_CHANGED';
  static readonly DOG_MOTHER_BREED_CHANGED = 'DOG_MOTHER_BREED_CHANGED';
  static readonly DOG_MOTHER_BREED_TYPE_CHANGED = 'DOG_MOTHER_BREED_TYPE_CHANGED';
  static readonly DOG_SIZE_CHANGED = 'DOG_SIZE_CHANGED';
  static readonly CAT_KEEPING_TYPE_CHANGED = 'CAT_KEEPING_TYPE_CHANGED';
  static readonly HAS_ILLNESSES_CHANGED = 'HAS_ILLNESSES_CHANGED';
  static readonly SELECTED_ILLNESSES_CHANGED = 'SELECTED_ILLNESSES_CHANGED';
  static readonly SELBSTBETEILIGUNG_CHANGED = 'SELBSTBETEILIGUNG_CHANGED';
  static readonly COVERAGE_TYPE_CHANGED = 'COVERAGE_TYPE_CHANGED';
  static readonly WANTS_COVERAGE_UPGRADE_CHANGED = 'WANTS_COVERAGE_UPGRADE_CHANGED';
  static readonly USE_EXISTING_WECHSELBAUSTEIN_CHANGED = 'USE_EXISTING_WECHSELBAUSTEIN_CHANGED';
  static readonly CONTINUE_TO_DASHBOARD = 'CONTINUE_TO_DASHBOARD';
  static readonly PRODUCT_LOADING_IN_PROGRESS = 'PRODUCT_LOADING_IN_PROGRESS';

  static readonly PRODUKTLINIEN_CHANGED = 'PRODUKTLINIEN_CHANGED';
  static readonly ANTRAGSNUMMER_GENERATED = 'ANTRAGSNUMMER_GENERATED';
  static readonly PREISE_REQUESTED = 'PREISE_REQUESTED';
  static readonly VORSCHLAGDATEN_CHANGED = 'VORSCHLAGDATEN_CHANGED';
  static readonly ZUSATZOPTION_CHECKED = 'ZUSATZOPTION_CHECKED';
  static readonly ZUSATZOPTION_UNCHECKED = 'ZUSATZOPTION_UNCHECKED';
  static readonly HEILBEHANDLUNGSSCHUTZ_CHANGED = 'HEILBEHANDLUNGSSCHUTZ_CHANGED';
  static readonly ZUM_ANGEBOT_BUTTON_CLICKED = 'ZUM_ANGEBOT_BUTTON_CLICKED';
  static readonly QUOTEDB_REQUESTED = 'QUOTEDB_REQUESTED';
  static readonly VORBELEGUNG_FOR_TKV_ERSATZ_REQUESTED = 'VORBELEGUNG_FOR_TKV_ERSATZ_REQUESTED';
  static readonly PRODUKT_ZUSATZOPTIONEN_VORBELEGT = 'PRODUKT_ZUSATZOPTIONEN_VORBELEGT';
  static readonly TARIFWECHSEL_INCLUDED = 'TARIFWECHSEL_INCLUDED';
  static readonly PRODUKTLINIE_PREFILLED = 'PRODUKTLINIE_PREFILLED';
  static readonly ANIMAL_TYPE_PREFILLED = 'ANIMAL_TYPE_PREFILLED';
  static readonly HANDLE_TKV_NAVIGATION_TO_ANGEBOT = 'HANDLE_TKV_NAVIGATION_TO_ANGEBOT';
  static readonly EXPECTED_PRODUKTTABELLE_SET = 'EXPECTED_PRODUKTTABELLE_SET';
  static readonly EXPECTED_PRODUKTTABELLE_CLEARED = 'EXPECTED_PRODUKTTABELLE_CLEARED';

  backToShopShopModalOpen(): TkvAction {
    return { type: TkvActions.BACK_TO_SHOP_MODAL_OPEN };
  }

  backToShopModalClose(): TkvAction {
    return { type: TkvActions.BACK_TO_SHOP_MODAL_CLOSE };
  }

  animalTypeChangedTkv(animalType: TierartEnum): TkvAction<TierartEnum> {
    return { type: TkvActions.ANIMAL_TYPE_CHANGED, payload: animalType };
  }

  animalGenderChanged(animalGender: Tiergeschlecht): TkvAction<Tiergeschlecht> {
    return { type: TkvActions.ANIMAL_GENDER_CHANGED, payload: animalGender };
  }

  animalNameChanged(anmialName: string): TkvAction<string> {
    return { type: TkvActions.ANIMAL_NAME_CHANGED, payload: anmialName };
  }

  animalBirthDateChanged(animalBirthDate: string): TkvAction<string> {
    return {
      type: TkvActions.ANIMAL_BIRTH_DATE_CHANGED,
      payload: animalBirthDate,
    };
  }

  breedChanged(breed: ValueLabelElement): TkvAction<ValueLabelElement> {
    return { type: TkvActions.BREED_CHANGED, payload: breed };
  }

  dogBreedTypeChanged(dogBreedType: DogBreedType): TkvAction<DogBreedType> {
    return { type: TkvActions.DOG_BREED_TYPE_CHANGED, payload: dogBreedType };
  }

  dogMotherBreedChanged(dogMotherBreed: ValueLabelElement): TkvAction<ValueLabelElement> {
    return { type: TkvActions.DOG_MOTHER_BREED_CHANGED, payload: dogMotherBreed };
  }

  dogMotherBreedTypeChanged(dogMotherBreedType: DogBreedType): TkvAction<DogBreedType> {
    return { type: TkvActions.DOG_MOTHER_BREED_TYPE_CHANGED, payload: dogMotherBreedType };
  }

  dogSizeChanged(dogSize: DogSize): TkvAction<DogSize> {
    return { type: TkvActions.DOG_SIZE_CHANGED, payload: dogSize };
  }

  catKeepingTypeChanged(catKeepingType: Tierhaltung): TkvAction<Tierhaltung> {
    return { type: TkvActions.CAT_KEEPING_TYPE_CHANGED, payload: catKeepingType };
  }

  hasIllnessesChanged(hasIllnesses: boolean): TkvAction<boolean> {
    return { type: TkvActions.HAS_ILLNESSES_CHANGED, payload: hasIllnesses };
  }

  selectedIllnessesChanged(
    name: string,
    value: boolean,
  ): TkvAction<SelectedIllnessesChangedPayload> {
    return { type: TkvActions.SELECTED_ILLNESSES_CHANGED, payload: { name, value } };
  }

  selbstbeteiligungChanged(selbstbeteiligung: Selbstbeteiligung): TkvAction<Selbstbeteiligung> {
    return { type: TkvActions.SELBSTBETEILIGUNG_CHANGED, payload: selbstbeteiligung };
  }

  coverageTypeChanged(coverageType: CoverageType): TkvAction<CoverageType> {
    return { type: TkvActions.COVERAGE_TYPE_CHANGED, payload: coverageType };
  }

  wantsCoverageUpgradeChanged(wantsCoverageUpgrade: boolean): TkvAction<boolean> {
    return { type: TkvActions.WANTS_COVERAGE_UPGRADE_CHANGED, payload: wantsCoverageUpgrade };
  }

  useExistingWechselbausteinChanged(useExistingWechselbaustein: boolean): TkvAction<boolean> {
    return {
      type: TkvActions.USE_EXISTING_WECHSELBAUSTEIN_CHANGED,
      payload: useExistingWechselbaustein,
    };
  }

  continueToDashboard(): TkvAction {
    return { type: TkvActions.CONTINUE_TO_DASHBOARD };
  }

  productLoadingInProgress(isLoading: boolean): TkvAction<boolean> {
    return { type: TkvActions.PRODUCT_LOADING_IN_PROGRESS, payload: isLoading };
  }

  produktZusatzOptionenVorbelegt(
    vorbelegundaten: TkvV2Vorbelegungdaten,
  ): TkvAction<TkvV2Vorbelegungdaten> {
    return {
      type: TkvActions.PRODUKT_ZUSATZOPTIONEN_VORBELEGT,
      payload: vorbelegundaten,
    };
  }

  vorschlagdatenChanged(deferTarifierung = false): TkvAction<{ deferTarifierung: boolean }> {
    return deferTarifierung
      ? { type: TkvActions.VORSCHLAGDATEN_CHANGED, payload: { deferTarifierung } }
      : { type: TkvActions.VORSCHLAGDATEN_CHANGED };
  }

  tarifChecked(index: number): TkvAction<{ index: number }> {
    return { type: TARIF_CHECKED, payload: { index } };
  }

  produktlinienChanged(produktlinien: Produktlinie[]): TkvAction<Produktlinie[]> {
    return { type: TkvActions.PRODUKTLINIEN_CHANGED, payload: produktlinien };
  }

  antragsnummerGenerated(antragsnummer: string): TkvAction<AntragsummerGenerated> {
    return {
      type: TkvActions.ANTRAGSNUMMER_GENERATED,
      payload: { antragsnummer },
    };
  }

  preiseRequested(): TkvAction {
    return { type: TkvActions.PREISE_REQUESTED };
  }

  zusatzoptionChecked(name: string): TkvAction<ZusatzoptionCheckedPayload> {
    return { type: TkvActions.ZUSATZOPTION_CHECKED, payload: { name } };
  }

  zusatzoptionUnchecked(name: string): TkvAction<ZusatzoptionCheckedPayload> {
    return { type: TkvActions.ZUSATZOPTION_UNCHECKED, payload: { name } };
  }

  heilbehandlungsschutzChanged(
    schutzsumme: Heilbehandlungsschutz,
  ): TkvAction<Heilbehandlungsschutz> {
    return {
      type: TkvActions.HEILBEHANDLUNGSSCHUTZ_CHANGED,
      payload: schutzsumme,
    };
  }

  zumAngebotButtonClicked(): TkvAction {
    return { type: TkvActions.ZUM_ANGEBOT_BUTTON_CLICKED };
  }

  quoteDBRequested(): TkvAction {
    return { type: TkvActions.QUOTEDB_REQUESTED };
  }

  vorbelegungForTkvRequested(
    vorbelegungdaten: TkvV2Vorbelegungdaten,
  ): GlobalAction<TkvV2Vorbelegungdaten> {
    return {
      type: TkvActions.VORBELEGUNG_FOR_TKV_ERSATZ_REQUESTED,
      payload: vorbelegungdaten,
    };
  }

  tarifwechselIncluded(included: boolean): TkvAction<boolean> {
    return { type: TkvActions.TARIFWECHSEL_INCLUDED, payload: included };
  }

  produktliniePrefilled(produktlinienname: Produktlinienname): TkvAction<Produktlinienname> {
    return { type: TkvActions.PRODUKTLINIE_PREFILLED, payload: produktlinienname };
  }

  animalTypePrefilled(animalType: TierartEnum): TkvAction<TierartEnum> {
    return { type: TkvActions.ANIMAL_TYPE_PREFILLED, payload: animalType };
  }

  handleTkvNavigationToAngebot(): TkvAction {
    return { type: TkvActions.HANDLE_TKV_NAVIGATION_TO_ANGEBOT };
  }

  expectedProdukttabelleSet(produkttabelle: ProdukttabelleEnum): TkvAction<ProdukttabelleEnum> {
    return { type: TkvActions.EXPECTED_PRODUKTTABELLE_SET, payload: produkttabelle };
  }

  expectedProdukttabelleCleared(): TkvAction {
    return { type: TkvActions.EXPECTED_PRODUKTTABELLE_CLEARED };
  }
}

export interface ZusatzoptionCheckedPayload {
  name: string;
}

export interface SelectedIllnessesChangedPayload {
  name: string;
  value: boolean;
}
